import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Grid } from '@mui/material';
import { colors } from 'remote/global';

interface Props {
  children: React.ReactNode;
}

function FullCalendarContainer({ children }: Props) {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Grid
      item
      xs={12}
      sx={{
        position: 'relative',
        '& .fc-view-harness': {
          minHeight: ['90vh', 'auto'],
        },
        '& .fc-h-event': {
          boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.04)',
          borderRadius: '8px',
          cursor: 'pointer',
        },
        '& .fc-license-message': {
          display: 'none !important',
        },
        '& tr.fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky span.fc-datagrid-cell-main':
          {
            display: 'none',
          },

        '& span.fc-datagrid-cell-main': {
          color: colors.uiBlue600,
        },
        '& .fc-event': {
          border: 'none!important',
          padding: 0,
        },
        '& table.fc-scrollgrid': {
          borderRadius: '8px',
        },
        'th:first-of-type .fc-datagrid-cell': {
          borderTopLeftRadius: '8px !important',
        },
        'th:last-child .fc-timeline-slot:last-child.fc-timeline-slot-label.fc-slot.fc-slot-wed.fc-slot-today.fc-slot-future':
          {
            borderTopRightRadius: '8px',
          },
        'th[role="presentation"]': {
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        },
        'tr.fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > th:first-of-type[role="presentation"]':
          {
            borderTopRightRadius: '0',
          },
        'tr.fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky span.fc-datagrid-cell-main':
          {
            color: colors.gray1,
            fontWeight: 600,
          },
        'tr.fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky a.fc-timeline-slot-cushion.fc-scrollgrid-sync-inner':
          {
            color: colors.gray1,
            fontWeight: 600,
          },
        'h2#fc-dom-1, .fc .fc-toolbar-title': {
          fontWeight: 600,
          color: colors.gray2,
        },
        '.fc .fc-toolbar': {
          justifyContent: 'flex-start',
          flexDirection: smallScreen ? 'column' : 'row',
          alignItems: smallScreen ? 'flex-start' : 'center',
        },
        '.fc-toolbar-chunk:nth-of-type(2)': {
          flex: 1,
          margin: `${smallScreen ? '12px' : 0} 0`,
        },
        'button.fc-today-button.fc-button.fc-button-primary': {
          marginRight: '12px',
        },
        '.fc-button-group button:first-of-type': {
          borderRadius: '8px 0 0 8px',
        },
        '.fc-button-group button:last-child': {
          borderRadius: '0 8px 8px 0',
        },
        '.fc .fc-button-primary': {
          color: colors.uiBlue600,
          background: 'white',
          paddingTop: '10px',
          paddingBottom: '10px',
          border: `1px solid ${colors.gray4}`,
          borderRadius: '8px',
        },
        '.fc .fc-timeline-overlap-enabled .fc-timeline-lane-frame .fc-timeline-events': {
          paddingBottom: 0,
        },
        '.fc-button-group button': {
          paddingTop: '10px',
          paddingBottom: '10px',
          color: colors.uiBlue600,
          background: 'white',
          border: `1px solid ${colors.gray4}`,
        },
        '.fc-button-group button:hover, .fc .fc-button-primary:hover, .fc .fc-button-primary:disabled':
          {
            color: colors.uiBlue600,
            background: colors.uiBlue50,
            border: `1px solid ${colors.gray4}`,
          },
        '.fc .fc-button-group > .fc-button.fc-button-active': {
          color: colors.uiBlue600,
          background: colors.uiBlue50,
          border: `1px solid ${colors.gray4}`,
        },
        '.fc .fc-button-primary:not(:disabled):active': {
          color: colors.uiBlue600,
          background: colors.uiBlue50,
          border: `1px solid ${colors.gray4}`,
        },
        '.fc .fc-button-primary:focus, .fc .fc-button-primary:not(:disabled).fc-button-active:focus, .fc .fc-button-primary:not(:disabled):active:focus':
          {
            boxShadow: 'none',
          },
        'th.fc-datagrid-cell.fc-resource-group.fc-cell-shaded span': {
          color: colors.gray2,
        },
        'td.fc-datagrid-cell.fc-resource, .fc .fc-scroller-harness': {
          background: 'white',
        },
        'tr.fc-scrollgrid-section.fc-scrollgrid-section-header .fc-scroller-harness': {
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        },
        'tr.fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky .fc-scroller-harness':
          {
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          },
        '.fc .fc-timeline-header-row-chrono .fc-timeline-slot-frame': {
          justifyContent: 'center',
        },
        '& .fc-timeline-more-link': {
          background: 'none',
          fontSize: '12px',
          lineHeight: '14px',
          padding: 0,
          margin: '0!important',
          border: 'none',
          '.fc-timeline-more-link-inner': {
            padding: 0,
          },
        },
        'th[role="columnheader"].fc-datagrid-cell .fc-datagrid-cell-cushion': {
          display: 'inline-flex',
          '.fc-datagrid-cell-main': {
            display: 'flex',
            alignItems: 'center',
            color: colors.textColor500,
          },
        },
        'thead, td[role="gridcell"].fc-datagrid-cell.fc-resource': {
          '.fc-icon, .fc-datagrid-expander': {
            display: 'none',
          },
        },
        '& .fc-datagrid-cell-frame': {
          '.fc-datagrid-cell-cushion': {
            padding: '6px',
          },
        },
        '& .fc-more-popover': {
          zIndex: '999!important',
        },
        '& .fc-popover-title': {
          fontSize: '14px',
        },
      }}
    >
      {children}
    </Grid>
  );
}

export default FullCalendarContainer;
